import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { PARTNER_PLAN_KEYS } from '../../../utils/partnerAcceptPlans';
import {
  formatPartnerPlanAmount,
  getPartnerSubscriptionPlanPresentation,
} from '../../../utils/partnerSubscriptionPlans';
import { usePortalTokens } from '../../../styles/usePortalTokens';

const PLAN_ICONS = {
  [PARTNER_PLAN_KEYS.PRO]: ApartmentOutlinedIcon,
  [PARTNER_PLAN_KEYS.PREMIUM]: GroupsOutlinedIcon,
  [PARTNER_PLAN_KEYS.BASIC]: PublicOutlinedIcon,
};

const DARK_NEUTRAL_AMOUNT = '#1C1917';

const PartnerPlanCard = ({ plan, isCurrent = false, isBusy = false, onSelect }) => {
  const theme = useTheme();
  const portal = usePortalTokens();
  const isDark = theme.palette.mode === 'dark';
  const presentation = getPartnerSubscriptionPlanPresentation(plan.key);
  const Icon = PLAN_ICONS[plan.key] ?? PublicOutlinedIcon;
  const currency = plan.currency || 'AED';

  const amountColor =
    presentation.amountColor === DARK_NEUTRAL_AMOUNT ? portal.text : presentation.amountColor;

  const headerColor =
    isDark && presentation.headerColor === DARK_NEUTRAL_AMOUNT
      ? portal.surfaceMuted
      : presentation.headerColor;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
        borderRadius: { xs: '16px', md: '20px' },
        overflow: 'hidden',
        bgcolor: portal.surface,
        border: '1px solid',
        borderColor: isCurrent ? headerColor : portal.border,
        boxShadow: isCurrent ? `0 10px 28px ${headerColor}33` : portal.cardShadow,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          bgcolor: headerColor,
          color: '#fff',
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 2.5, sm: 3 },
          minHeight: { xs: 132, sm: 148 },
        }}
      >
        {presentation.badge ? (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              px: 1.25,
              py: 0.35,
              borderRadius: 999,
              bgcolor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.92)',
              color: isDark ? '#fff' : headerColor,
              fontSize: '0.65rem',
              fontWeight: 800,
              letterSpacing: '0.04em',
              maxWidth: 'calc(100% - 24px)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {presentation.badge}
          </Box>
        ) : null}

        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Box>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            lineHeight: 1.2,
            mb: 0.75,
            pr: presentation.badge ? 10 : 0,
            wordBreak: 'break-word',
          }}
        >
          {plan.name}
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, opacity: 0.92 }}>
          {presentation.description}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 2, sm: 2.5 },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 0.75,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', sm: '2rem' },
              lineHeight: 1,
              color: amountColor,
            }}
          >
            {formatPartnerPlanAmount(plan.amount)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {currency}/mo
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 2.5, flex: 1 }}>
          {presentation.features.map((feature) => (
            <Box key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CheckCircleIcon
                sx={{ fontSize: 18, color: 'success.main', mt: '1px', flexShrink: 0 }}
              />
              <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        {isCurrent ? (
          <Button
            fullWidth
            disabled
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '12px',
              py: 1.1,
              borderColor: portal.border,
              color: 'text.secondary',
            }}
          >
            Current plan
          </Button>
        ) : (
          <LoadingButton
            fullWidth
            variant="contained"
            loading={isBusy}
            onClick={() => onSelect?.(plan)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '12px',
              py: 1.1,
              bgcolor: headerColor,
              color: '#fff',
              '&:hover': {
                bgcolor: headerColor,
                filter: isDark ? 'brightness(1.08)' : 'brightness(0.92)',
              },
            }}
          >
            Choose {plan.name}
          </LoadingButton>
        )}
      </Box>
    </Box>
  );
};

export default PartnerPlanCard;
